import { v2_CART_SESSION_KEY } from '@crocodille/common';
import { omit } from '@crocodille/util';
import { destroyCookie, parseCookies, setCookie } from 'nookies';

import {
  SESSION_COOKIE_KEY,
  CART_SESSION_COOKIE_KEY,
  STORAGE_COOKIE_KEY,
} from './storage.keys.mjs';

import type { IncomingMessage, ServerResponse } from 'http';
import type { NextApiRequest, NextApiResponse } from 'next';

export const SESSION_COOKIE_OPTIONS = {
  path: '/',
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'lax',
  enc: JSON.stringify,
  dec: JSON.parse,
} as const;

export const STORAGE_COOKIE_OPTIONS = {
  path: '/',
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'lax',
  maxAge: 14 * 24 * 60 * 60, // Two weeks (value is in seconds)
  enc: JSON.stringify,
  dec: JSON.parse,
} as const;

export const CART_SESSION_COOKIE_OPTIONS = {
  ...SESSION_COOKIE_OPTIONS,
  enc: encodeURIComponent,
  dec: decodeURIComponent,
} as const;

export const ANALYTICS_COOKIE_OPTIONS = {
  ...STORAGE_COOKIE_OPTIONS,
} as const;

export function initStorage(ctx: {
  readonly req?: IncomingMessage | NextApiRequest;
  readonly res?: ServerResponse | NextApiResponse;
}) {
  const cookies = parseCookies(ctx);
  const session = cookies[SESSION_COOKIE_KEY] as unknown as
    | Partial<Record<string, unknown>>
    | undefined;
  const cart = cookies[CART_SESSION_COOKIE_KEY];
  const storage = cookies[STORAGE_COOKIE_KEY] as unknown as
    | Partial<Record<string, unknown>>
    | undefined;

  const initialSession = {
    ...session,
    [v2_CART_SESSION_KEY]: cart,
  };

  const initialStorage = storage ?? {};

  function onSessionChange(session: Partial<Record<string, unknown>>) {
    const cart = session[v2_CART_SESSION_KEY];
    session = omit(session, v2_CART_SESSION_KEY);

    setCookie(
      ctx,
      SESSION_COOKIE_KEY,
      session as unknown as string,
      SESSION_COOKIE_OPTIONS,
    );

    if (cart) {
      setCookie(
        ctx,
        CART_SESSION_COOKIE_KEY,
        cart as unknown as string,
        CART_SESSION_COOKIE_OPTIONS,
      );
    } else {
      destroyCookie(ctx, CART_SESSION_COOKIE_KEY, CART_SESSION_COOKIE_OPTIONS);
    }
  }

  function onStorageChange(storage: Partial<Record<string, unknown>>) {
    setCookie(
      ctx,
      STORAGE_COOKIE_KEY,
      storage as unknown as string,
      STORAGE_COOKIE_OPTIONS,
    );
  }

  return {
    initialSession,
    initialStorage,
    onSessionChange,
    onStorageChange,
  };
}
